import { AssetPermission, AssetPermissionsInReduxStore } from '../../types/asset-permissions.types'
import { RootState } from '../../../../redux/types'
import {
  addDraftPermissionToAsset,
  deleteAssetPermissions,
  removeDraftPermissionFromAsset,
  resetDraftPermissions as resetDraftPermissionsAction,
  updateDraftPermissionOnAsset
} from '@dtx-company/inter-app/src/redux/slices/flowteams/assetPermissionsSlice'
import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useInitializeAssetPermissionsState } from './useInitializeAssetPermissionsState'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import xorWith from 'lodash/xorWith'

export interface AssetPermissionsState extends AssetPermissionsInReduxStore {
  resetDraftPermissions: () => void
  addDraftPermission: (p: AssetPermission) => void
  removeDraftPermission: (p: AssetPermission) => void
  updateDraftPermission: (p: AssetPermission) => void
  deleteAssetPermissions: (assetId: string) => void
  draftPermissionsAreDifferentFromCurrentPermissions: boolean
}

export function useAssetPermissionsState(
  assetId: string,
  isWhiteLabel?: boolean,
  isCodeSubdomain?: boolean
): AssetPermissionsState | null {
  useInitializeAssetPermissionsState(assetId, isWhiteLabel, isCodeSubdomain)

  const thisAssetPermissions = useSelector(
    (state: RootState) => state.flowteamsReducer.assetPermissions[assetId]
  )
  const dispatch = useDispatch()
  const resetDraftPermissions = useCallback(() => {
    dispatch(resetDraftPermissionsAction({ assetId }))
  }, [dispatch, assetId])

  const _deleteAssetPermissions = useCallback(
    (assetId: string) => {
      dispatch(deleteAssetPermissions({ assetId }))
    },
    [dispatch]
  )

  const addDraftPermission = useCallback(
    (p: AssetPermission) => {
      dispatch(addDraftPermissionToAsset({ newDraftPermission: p, assetId }))
    },
    [dispatch, assetId]
  )

  const removeDraftPermission = useCallback(
    (p: AssetPermission) => {
      dispatch(removeDraftPermissionFromAsset({ entityId: p.entityId, assetId }))
    },
    [dispatch, assetId]
  )

  const updateDraftPermission = useCallback(
    (p: AssetPermission) => {
      dispatch(
        updateDraftPermissionOnAsset({ updatedPermission: p, entityId: p.entityId, assetId })
      )
    },
    [dispatch, assetId]
  )

  const draftPermissionsAreDifferentFromCurrentPermissions = isEmpty(
    xorWith(
      thisAssetPermissions?.currentPermissions,
      thisAssetPermissions?.draftPermissions,
      isEqual
    )
  )

  const assetPermissionsState = useMemo(
    () => ({
      ...thisAssetPermissions,
      resetDraftPermissions,
      addDraftPermission,
      removeDraftPermission,
      updateDraftPermission,
      deleteAssetPermissions: _deleteAssetPermissions,
      draftPermissionsAreDifferentFromCurrentPermissions
    }),
    [
      thisAssetPermissions,
      resetDraftPermissions,
      addDraftPermission,
      removeDraftPermission,
      updateDraftPermission,
      _deleteAssetPermissions,
      draftPermissionsAreDifferentFromCurrentPermissions
    ]
  )

  return !thisAssetPermissions ? null : assetPermissionsState
}
